import { useCallback, useEffect, useState } from 'react';
import { useContentProgress } from 'ContentProgress';
import { getStreamAndEnsureStored } from 'StoredContent';
import { type StreamForStreamDashboard } from 'Lessons';
import { adaptStoredStreamForDashboard } from './adaptStoredStreamForDashboard';
import { type StoredStreamForDashboard } from '../StreamDashboard.types';

/*
    Loads the stream behind the dashboard out of the offline store. StreamDashboardComponent uses this
    instead of useStreamForDashboard while the app is in offline mode.

    This is a separate hook, and a separate component branch above it, because of useContentProgress:
    it suspends forever without a current user, so it cannot sit in the hook that also serves
    signed-out visitors. Offline mode always implies a signed-in user, so it is safe here.
*/
export function useStoredStreamForDashboard(streamId: string): {
    stream: StreamForStreamDashboard | null;
    isLoading: boolean;
    hasError: boolean;
    retry: () => void;
} {
    const contentProgress = useContentProgress();
    const [storedStream, setStoredStream] = useState<StoredStreamForDashboard | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        /*
            A store miss and a store failure both have to end the load. Without this the page keeps
            no stream and no error, so the spinner runs for ever. The Angular page got the
            ApiErrorHandler modal here.
        */
        getStreamAndEnsureStored({ id: streamId })
            .then(stream => {
                if (cancelled) return;
                setStoredStream(stream);
                setHasError(!stream);
                setIsLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setStoredStream(null);
                setHasError(true);
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [streamId, attempt]);

    const retry = useCallback(() => setAttempt(current => current + 1), []);

    return {
        stream: storedStream ? adaptStoredStreamForDashboard(storedStream, contentProgress) : null,
        isLoading,
        hasError,
        retry,
    };
}
